import { useEffect, useRef, useState } from 'react'
import { HStack, IconButton, Menu, Portal, Spinner } from '@chakra-ui/react'
import { LuEllipsis } from 'react-icons/lu'
import { Tooltip } from './tooltip'
import { fitActions } from '../../utils/overflowActions'

/**
 * A row of small icon buttons that never spills out of its container: what
 * does not fit moves into a "⋯" menu, where each action is listed by name.
 * The row measures itself, so it follows a card resized by the grid, the
 * window or the sidebar alike.
 *
 * @param {{actions: Array<{key: string, label: string, icon: JSX.Element,
 *     onClick: function, color?: string, danger?: boolean, loading?: boolean,
 *     disabled?: boolean, tooltip?: string, menuOnly?: boolean}>,
 *   overflowOrder?: Array<string>, moreLabel: string}} props
 *   `overflowOrder` names the actions that leave the row first; a `menuOnly`
 *   action is always in the menu and never takes a slot (fitActions).
 */
export function OverflowActionRow({ actions, overflowOrder, moreLabel }) {
    const rowRef = useRef(null)
    // An always-rendered, invisible copy of the menu button: every button in
    // the row is this size, so it is what the fit is measured in.
    const probeRef = useRef(null)
    const [space, setSpace] = useState({ available: 0, buttonWidth: 0, gap: 0 })

    useEffect(() => {
        const row = rowRef.current
        if (!row) return
        const observer = new ResizeObserver(() => {
            setSpace({
                available: row.clientWidth,
                buttonWidth: probeRef.current?.offsetWidth || 0,
                gap: parseFloat(getComputedStyle(row).columnGap) || 0,
            })
        })
        observer.observe(row)
        // The probe too: in a tab that has not been laid out yet it measures
        // 0, which reads as "not measured" and shows every button, and the
        // row may never resize again to correct it.
        if (probeRef.current) observer.observe(probeRef.current)
        return () => observer.disconnect()
    }, [])

    const { visible, overflow } = fitActions(actions, { ...space, overflowOrder })

    return (
        <HStack ref={rowRef} gap={2} width="100%" minW={0} position="relative">
            <IconButton
                ref={probeRef}
                size="sm"
                variant="ghost"
                aria-hidden
                tabIndex={-1}
                position="absolute"
                visibility="hidden"
                pointerEvents="none"
            >
                <LuEllipsis />
            </IconButton>

            {visible.map((a) => (
                <Tooltip key={a.key} content={a.tooltip ?? a.label} openDelay={300} closeDelay={100}>
                    <IconButton
                        size="sm"
                        variant="ghost"
                        flexShrink={0}
                        aria-label={a.label}
                        color={a.color}
                        colorPalette={a.danger ? 'red' : undefined}
                        disabled={a.disabled || a.loading}
                        onClick={a.onClick}
                    >
                        {a.loading ? <Spinner size="sm" /> : a.icon}
                    </IconButton>
                </Tooltip>
            ))}

            {overflow.length > 0 && (
                <Menu.Root positioning={{ placement: 'bottom-end' }}>
                    <Menu.Trigger asChild>
                        <IconButton size="sm" variant="ghost" flexShrink={0} aria-label={moreLabel} title={moreLabel}>
                            <LuEllipsis />
                        </IconButton>
                    </Menu.Trigger>
                    <Portal>
                        <Menu.Positioner>
                            <Menu.Content minW="180px">
                                {overflow.map((a) => (
                                    <Menu.Item
                                        key={a.key}
                                        value={a.key}
                                        color={a.danger ? 'fg.error' : undefined}
                                        disabled={a.disabled || a.loading}
                                        onClick={a.onClick}
                                    >
                                        <span style={{ display: 'inline-flex', color: a.danger ? undefined : a.color }}>
                                            {a.loading ? <Spinner size="xs" /> : a.icon}
                                        </span>
                                        {a.label}
                                    </Menu.Item>
                                ))}
                            </Menu.Content>
                        </Menu.Positioner>
                    </Portal>
                </Menu.Root>
            )}
        </HStack>
    )
}
